/**
 * Feb 2026 · Mobile hooks + guards.
 *
 * `useIsMobile()`  — returns `true` when the viewport is narrower than
 *                    the Tailwind `md` breakpoint (768 px). Kept
 *                    dependency-free so it works in the layout, guard,
 *                    and any read-only enforcement site.
 *
 * `MobileGuard`    — wraps every child route. On mobile it redirects
 *                    non-dashboard URLs to `/sales-dashboard`. Whitelist
 *                    is dashboards + login/catalogue (public pages skip
 *                    the guard because they are rendered outside the
 *                    `<Protected/Layout>` tree in App.js).
 *
 * The mobile-only read-only enforcement (hide Add/New/Edit/Delete/
 * Authorize buttons + disable inputs) is delivered via CSS in
 * `index.css` — see the `@media (max-width: 767px)` block there.
 */
import { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";

const BREAKPOINT_PX = 768;

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < BREAKPOINT_PX : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(`(max-width: ${BREAKPOINT_PX - 1}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    // Safari <14 uses addListener; modern browsers use addEventListener.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    setIsMobile(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

// Dashboard whitelist — every route in this list stays reachable on
// mobile. Everything else redirects to `/sales-dashboard`. Prefixes are
// matched via startsWith so nested reports/tools remain guarded.
const DASHBOARD_ROUTES = [
  "/sales-dashboard",
  "/dashboards/purchase",
  "/dashboards/inventory",
];

// Root `/` renders the main production Dashboard component (see App.js
// `<Route index element={<Dashboard />} />`) — permitted as-is.
function isAllowedOnMobile(pathname) {
  if (pathname === "/") return true;
  return DASHBOARD_ROUTES.some(r => pathname === r || pathname.startsWith(r + "/"));
}

export function MobileGuard({ children }) {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  if (isMobile && !isAllowedOnMobile(pathname)) {
    return <Navigate to="/sales-dashboard" replace />;
  }
  return children;
}
